import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { BACKEND_URL } from '../lib/api';

/**
 * Free waitlist sign-up dialog. Opens from EventDetailPage when a ticket type
 * is sold out. Customers leave name + email and get notified the moment a
 * seat opens up (via /api/events/{event_id}/waitlist).
 */
const WaitlistDialog = ({ open, onOpenChange, event, ticket }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const reset = () => {
    setForm({ name: '', email: '', phone: '' });
    setJoined(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/events/${event.event_id}/waitlist`,
        {
          ticket_type_id: ticket.ticket_id,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
        }
      );
      const status = res.data?.status;
      if (status === 'already_on_waitlist') {
        toast.info('You are already on the waitlist for this ticket type.');
      } else {
        toast.success("You're on the waitlist. We'll email you the moment a seat opens.");
      }
      setJoined(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join the waitlist.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="waitlist-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#1F3D2B] flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-[#C9A27A]" />
            </div>
            <DialogTitle
              className="text-2xl"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Join the waitlist
            </DialogTitle>
          </div>
          <DialogDescription className="text-[#4A5D50] text-sm leading-relaxed">
            <span className="font-medium text-[#112318]">
              {ticket?.type_name || 'This ticket'}
            </span>{' '}
            for <span className="italic">{event?.title}</span> is currently sold out. We&apos;ll email
            you the moment a seat opens up — first come, first served. No fee, no spam.
          </DialogDescription>
        </DialogHeader>

        {joined ? (
          <div
            className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-4 text-sm leading-relaxed"
            data-testid="waitlist-joined-notice"
          >
            You&apos;re all set. Look out for an email from VinePass — book promptly when it arrives,
            seats go quickly.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input
                data-testid="waitlist-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                data-testid="waitlist-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input
                data-testid="waitlist-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="waitlist-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                data-testid="waitlist-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…
                  </>
                ) : (
                  'Join waitlist'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
